export type ClientLogLevel = 'error' | 'warn'

export type ClientLogContextValue = string | number | boolean | null

export type ClientLogContext = Record<string, ClientLogContextValue>

export type ClientLogError = {
    name?: string
    code?: string
    message?: string
    stack?: string
}

export type ClientLogApp = 'admin' | 'client'
export type ClientLogDeviceType = 'mobile' | 'tablet' | 'desktop' | 'bot' | 'unknown'
export type ClientLogDisplayMode = 'browser' | 'standalone' | 'fullscreen' | 'minimal-ui' | 'window-controls-overlay' | 'unknown'

export type ClientLogDeviceMeta = {
    deviceType?: ClientLogDeviceType
    osFamily?: string
    browserFamily?: string
    language?: string
    timezone?: string
    viewport?: string
    touchPoints?: number
    displayMode?: ClientLogDisplayMode
    standalone?: boolean
}

export type ClientLogPayloadV2 = {
    schema: 'v2'
    app: ClientLogApp
    level: ClientLogLevel
    event: string
    message: string
    source: string
    route?: string
    userAgent?: string
    device?: ClientLogDeviceMeta
    correlationId?: string
    requestId: string
    tags?: string[]
    context?: ClientLogContext
    error?: ClientLogError
    ts: string
}

export const MAX_MESSAGE_LENGTH = 1000
export const MAX_SOURCE_LENGTH = 128
const MAX_EVENT_LENGTH = 64
export const MAX_ROUTE_LENGTH = 512
export const MAX_USER_AGENT_LENGTH = 512
const MAX_DEVICE_OS_FAMILY_LENGTH = 64
const MAX_DEVICE_BROWSER_FAMILY_LENGTH = 64
const MAX_DEVICE_LANGUAGE_LENGTH = 24
const MAX_DEVICE_TIMEZONE_LENGTH = 64
const MAX_DEVICE_VIEWPORT_LENGTH = 11
const MAX_DEVICE_TOUCH_POINTS = 20
const MAX_TAG_LENGTH = 64
const MAX_TAGS = 12
const MAX_CONTEXT_KEYS = 64
const MAX_CONTEXT_KEY_LENGTH = 64
const MAX_CONTEXT_VALUE_LENGTH = 1000
const MAX_ERROR_NAME_LENGTH = 120
const MAX_ERROR_CODE_LENGTH = 80
const MAX_ERROR_MESSAGE_LENGTH = 1000
const MAX_STACK_LENGTH = 5000
export const MAX_REQUEST_ID_LENGTH = 80
export const MAX_CORRELATION_ID_LENGTH = 80
export const MAX_BODY_BYTES = 64 * 1024

const SENSITIVE_CONTEXT_KEY = /(token|secret|password|authorization|cookie|jwt|sessiontoken|sessioncookie)/i

export const clampString = (value: string | undefined, max: number): string | undefined => {
    if (typeof value !== 'string') return undefined
    if (value.length <= max) return value
    return value.slice(0, max)
}

const sanitizeKey = (value: string, max: number): string => {
    const lowered = value.toLowerCase().replace(/[^a-z0-9._-]+/g, '_')
    return lowered.slice(0, max)
}

export const sanitizeEventName = (value: string): string => {
    const sanitized = sanitizeKey(value, MAX_EVENT_LENGTH)
    return sanitized || 'unknown_event'
}

export const sanitizeSource = (value: string): string => {
    const sanitized = sanitizeKey(value, MAX_SOURCE_LENGTH)
    return sanitized || 'unknown_source'
}

const normalizeDeviceToken = (value: string, max: number): string | undefined => {
    const normalized = value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9._-]+/g, '_')

    return clampString(normalized || undefined, max)
}

const sanitizeDeviceType = (value: unknown): ClientLogDeviceType | undefined => {
    if (typeof value !== 'string') return undefined
    const normalized = normalizeDeviceToken(value, 16)
    if (normalized === 'mobile' || normalized === 'tablet' || normalized === 'desktop' || normalized === 'bot' || normalized === 'unknown') {
        return normalized
    }
    return undefined
}

const sanitizeDeviceDisplayMode = (value: unknown): ClientLogDisplayMode | undefined => {
    if (typeof value !== 'string') return undefined
    const normalized = normalizeDeviceToken(value, 32)
    if (
        normalized === 'browser' ||
        normalized === 'standalone' ||
        normalized === 'fullscreen' ||
        normalized === 'minimal-ui' ||
        normalized === 'window-controls-overlay' ||
        normalized === 'unknown'
    ) {
        return normalized
    }
    return undefined
}

const sanitizeDeviceViewport = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined
    const normalized = value.trim().toLowerCase()
    if (!/^\d{2,5}x\d{2,5}$/.test(normalized)) return undefined
    return clampString(normalized, MAX_DEVICE_VIEWPORT_LENGTH)
}

const sanitizeDeviceTimezone = (value: unknown): string | undefined => {
    if (typeof value !== 'string') return undefined
    const normalized = value
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9/_+-]+/g, '_')

    return clampString(normalized || undefined, MAX_DEVICE_TIMEZONE_LENGTH)
}

const sanitizeDeviceTouchPoints = (value: unknown): number | undefined => {
    if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value)) {
        return undefined
    }
    if (value < 0 || value > MAX_DEVICE_TOUCH_POINTS) {
        return undefined
    }
    return value
}

const sanitizeContextValue = (value: unknown): ClientLogContextValue | undefined => {
    if (typeof value === 'string') {
        return clampString(value, MAX_CONTEXT_VALUE_LENGTH) ?? ''
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : undefined
    }
    if (typeof value === 'boolean' || value === null) {
        return value
    }
    return undefined
}

export const sanitizeContext = (value: unknown): ClientLogContext | undefined => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return undefined
    }

    const out: ClientLogContext = {}
    let count = 0

    for (const [rawKey, rawValue] of Object.entries(value as Record<string, unknown>)) {
        if (count >= MAX_CONTEXT_KEYS) break

        const key = sanitizeKey(rawKey, MAX_CONTEXT_KEY_LENGTH)
        if (!key || SENSITIVE_CONTEXT_KEY.test(key)) continue

        const sanitizedValue = sanitizeContextValue(rawValue)
        if (sanitizedValue === undefined) continue

        out[key] = sanitizedValue
        count += 1
    }

    return Object.keys(out).length > 0 ? out : undefined
}

export const sanitizeTags = (value: unknown): string[] | undefined => {
    if (!Array.isArray(value)) return undefined

    const out: string[] = []
    for (const raw of value) {
        if (out.length >= MAX_TAGS) break
        if (typeof raw !== 'string') continue
        const tag = sanitizeKey(raw, MAX_TAG_LENGTH)
        if (!tag) continue
        out.push(tag)
    }

    return out.length > 0 ? out : undefined
}

export const sanitizeError = (value: unknown): ClientLogError | undefined => {
    if (!value || typeof value !== 'object') return undefined

    const maybeError = value as {
        name?: unknown
        code?: unknown
        stack?: unknown
        message?: unknown
    }

    const name = typeof maybeError.name === 'string'
        ? clampString(maybeError.name, MAX_ERROR_NAME_LENGTH)
        : undefined

    const code = typeof maybeError.code === 'string'
        ? clampString(maybeError.code, MAX_ERROR_CODE_LENGTH)
        : undefined

    const message = typeof maybeError.message === 'string'
        ? clampString(maybeError.message, MAX_ERROR_MESSAGE_LENGTH)
        : undefined

    const stack = typeof maybeError.stack === 'string'
        ? clampString(maybeError.stack, MAX_STACK_LENGTH)
        : typeof maybeError.message === 'string'
            ? clampString(maybeError.message, MAX_STACK_LENGTH)
            : undefined

    if (!name && !code && !message && !stack) {
        return undefined
    }

    return {
        ...(name ? {name} : {}),
        ...(code ? {code} : {}),
        ...(message ? {message} : {}),
        ...(stack ? {stack} : {}),
    }
}

export const sanitizeDeviceMeta = (value: unknown): ClientLogDeviceMeta | undefined => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return undefined
    }

    const candidate = value as Record<string, unknown>
    const deviceType = sanitizeDeviceType(candidate.deviceType)
    const osFamily = normalizeDeviceToken(
        typeof candidate.osFamily === 'string' ? candidate.osFamily : '',
        MAX_DEVICE_OS_FAMILY_LENGTH
    )
    const browserFamily = normalizeDeviceToken(
        typeof candidate.browserFamily === 'string' ? candidate.browserFamily : '',
        MAX_DEVICE_BROWSER_FAMILY_LENGTH
    )
    const language = normalizeDeviceToken(
        typeof candidate.language === 'string' ? candidate.language : '',
        MAX_DEVICE_LANGUAGE_LENGTH
    )
    const timezone = sanitizeDeviceTimezone(candidate.timezone)
    const viewport = sanitizeDeviceViewport(candidate.viewport)
    const touchPoints = sanitizeDeviceTouchPoints(candidate.touchPoints)
    const displayMode = sanitizeDeviceDisplayMode(candidate.displayMode)
    const standalone = typeof candidate.standalone === 'boolean' ? candidate.standalone : undefined

    const out: ClientLogDeviceMeta = {
        ...(deviceType ? {deviceType} : {}),
        ...(osFamily ? {osFamily} : {}),
        ...(browserFamily ? {browserFamily} : {}),
        ...(language ? {language} : {}),
        ...(timezone ? {timezone} : {}),
        ...(viewport ? {viewport} : {}),
        ...(touchPoints !== undefined ? {touchPoints} : {}),
        ...(displayMode ? {displayMode} : {}),
        ...(standalone !== undefined ? {standalone} : {}),
    }

    return Object.keys(out).length > 0 ? out : undefined
}

export const isClientLogLevel = (value: unknown): value is ClientLogLevel => {
    return value === 'error' || value === 'warn'
}

export const isClientLogApp = (value: unknown): value is ClientLogApp => {
    return value === 'admin' || value === 'client'
}
